import type { ReactNode } from 'react';
import { usePermission } from '@hooks/usePermission';

interface PermissionGateProps {
  /** Permission code to check, e.g. "businesses.approve" */
  permission: string;
  /** Optional business scope: if set, checks business-level permissions too */
  businessId?: string;
  /** Content to show if the user has the permission */
  children: ReactNode;
  /** Content to show if the user does NOT have the permission (default: nothing) */
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on the logged-in user's permissions.
 * The frontend NEVER enforces security; this only controls visibility.
 * The backend enforces the real permission check on every API call.
 */
export function PermissionGate({ permission, businessId, children, fallback = null }: PermissionGateProps) {
  const { hasPermission } = usePermission();

  if (!hasPermission(permission, businessId)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
